import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { alertsApi } from '../../services/api';
import { hideCurrency } from '../../utils/format';
import { useValuesVisibility } from '../../stores/valuesVisibilityStore';
import { ASSET_TYPE_LABELS, ASSET_TYPE_BG, type AssetType, type Alert, type Asset } from '../../types';
import { PageLoader } from '../../components/common/LoadingSpinner';
import { BellIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useAssets } from '../../hooks/useAssets';

export default function BuyCalendarPage() {
  const { data: alerts, isLoading, refetch } = useQuery({
    queryKey: ['alerts'],
    queryFn: alertsApi.getAll,
  });
  const { data: assets } = useAssets();
  const { isVisible } = useValuesVisibility();
  const [showCreate, setShowCreate] = useState(false);

  const handleDelete = async (id: string) => {
    await alertsApi.remove(id);
    refetch();
  };

  const handleToggle = async (id: string, isActive: boolean) => {
    await alertsApi.update(id, { isActive: !isActive });
    refetch();
  };

  if (isLoading) return <PageLoader />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">Calendário de Compras</h1>
          <p className="page-subtitle">Alertas de preço e calendário de aquisições</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary">
          <PlusIcon className="h-4 w-4" />
          Novo Alerta
        </button>
      </div>

      {/* Alerts List */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <BellIcon className="h-5 w-5 text-warning-500" />
          <h2 className="text-sm font-semibold text-white">Alertas Configurados</h2>
        </div>

        {alerts && alerts.length > 0 ? (
          <div className="space-y-3">
            {alerts.map((alert: Alert) => (
              <div
                key={alert.id}
                className={`flex items-center justify-between p-4 rounded-lg border ${
                  alert.isActive
                    ? 'border-white/5 bg-white/[0.02]'
                    : 'border-white/5 bg-white/[0.02] opacity-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`h-2 w-2 rounded-full ${
                      alert.isActive ? 'bg-success-500' : 'bg-white/20'
                    }`}
                  />
                  <div>
                    <p className="text-sm font-medium text-white font-mono">
                      {alert.assets?.code || 'N/A'}
                    </p>
                    <p className="text-xs text-white/40">
                      {alert.alertType === 'PRICE_LOW'
                        ? `Preço abaixo de ${hideCurrency(alert.thresholdValue ?? 0, isVisible)}`
                        : alert.alertType === 'PRICE_HIGH'
                        ? `Preço acima de ${hideCurrency(alert.thresholdValue ?? 0, isVisible)}`
                        : 'Provento chegando'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggle(alert.id, alert.isActive)}
                    className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                      alert.isActive
                        ? 'border-success-500/30 text-success-500 hover:bg-success-500/10'
                        : 'border-white/10 text-white/40 hover:bg-white/5'
                    }`}
                  >
                    {alert.isActive ? 'Ativo' : 'Inativo'}
                  </button>
                  <button
                    onClick={() => handleDelete(alert.id)}
                    className="btn-ghost p-2 text-danger-500 hover:text-danger-400"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <BellIcon className="h-10 w-10 text-white/10 mx-auto mb-3" />
            <p className="text-white/30 text-sm">Nenhum alerta configurado</p>
            <button onClick={() => setShowCreate(true)} className="btn-primary mt-4">
              Criar Primeiro Alerta
            </button>
          </div>
        )}
      </div>

      {/* Indicators Panel */}
      <div className="card">
        <h2 className="text-sm font-semibold text-white mb-4">Indicadores Técnicos</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {assets?.slice(0, 6).map((asset) => {
            const currentPrice = asset.currentPrice ?? asset.averagePrice;
            const avgPrice = asset.averagePrice;
            const diff = currentPrice - avgPrice;
            const diffPct = avgPrice > 0 ? (diff / avgPrice) * 100 : 0;

            return (
              <div key={asset.id} className="p-4 rounded-lg bg-white/[0.03] border border-white/5">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono font-medium text-white">{asset.code}</span>
                  <span
                    className={`badge ${ASSET_TYPE_BG[asset.type as AssetType] || 'badge-blue'} text-xs`}
                  >
                    {ASSET_TYPE_LABELS[asset.type as AssetType]?.split(' ')[0]}
                  </span>
                </div>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-white/40">Preço atual</span>
                    <span className="font-mono text-white">{hideCurrency(currentPrice, isVisible)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/40">Preço médio</span>
                    <span className="font-mono text-white">{hideCurrency(avgPrice, isVisible)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/40">Variação</span>
                    <span
                      className={`font-mono ${
                        diffPct > 0 ? 'text-success-500' : diffPct < 0 ? 'text-danger-500' : 'text-white/40'
                      }`}
                    >
                      {diffPct > 0 ? '+' : ''}{diffPct.toFixed(2)}%
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showCreate && assets && (
        <CreateAlertModal
          assets={assets}
          onClose={() => { setShowCreate(false); refetch(); }}
        />
      )}
    </div>
  );
}

function CreateAlertModal({ assets, onClose }: { assets: Asset[]; onClose: () => void }) {
  const [form, setForm] = useState({
    assetId: '',
    alertType: 'PRICE_LOW',
    thresholdValue: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await alertsApi.create({
      assetId: form.assetId,
      alertType: form.alertType,
      thresholdValue: form.thresholdValue ? Number(form.thresholdValue) : undefined,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface-800 rounded-2xl border border-white/10 w-full max-w-md">
        <div className="p-6 border-b border-white/5">
          <h2 className="text-lg font-semibold text-white">Novo Alerta</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="label">Ativo</label>
            <select
              value={form.assetId}
              onChange={(e) => setForm({ ...form, assetId: e.target.value })}
              className="input"
              required
            >
              <option value="">Selecione um ativo...</option>
              {assets.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code} — {a.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Tipo de Alerta</label>
            <select
              value={form.alertType}
              onChange={(e) => setForm({ ...form, alertType: e.target.value })}
              className="input"
            >
              <option value="PRICE_LOW">Preço baixou para (comprar)</option>
              <option value="PRICE_HIGH">Preço subiu para (vender)</option>
              <option value="DIVIDEND_COMING">Provento chegando</option>
            </select>
          </div>

          {form.alertType !== 'DIVIDEND_COMING' && (
            <div>
              <label className="label">Valor Limite (R$)</label>
              <input
                type="number"
                step="any"
                value={form.thresholdValue}
                onChange={(e) => setForm({ ...form, thresholdValue: e.target.value })}
                className="input"
                required
              />
            </div>
          )}

          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancelar
            </button>
            <button type="submit" className="btn-primary flex-1">
              Criar Alerta
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
